import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../api/API';
import './APlanning.css';

import RefertoPDF from '../../components/pdf/Referto';
import { raggruppaTurniPerFase, partiteFuoriFase, titoloFase, coloreFase } from '../../components/planning/fasiPlanning';
import { TURNO_PANCHINA, inPanchina, postiPerTutte } from '../../components/planning/panchina';
import { doppiImpegniPerTurno, partiteInDoppioImpegno } from '../../components/planning/doppioImpegno';

// Il nome che finisce con " #<numero>" non e' una squadra ma un posto nella formula - "girone
// Girone A #3", cioe' la terza del girone A. Li' il numero e' l'informazione che distingue una riga
// dall'altra, quindi i puntini vanno sul nome del nodo e il numero resta sempre a video.
const NOME_CON_POSIZIONE = /^(.*\S)(\s*#\d+)$/;

const NomeSquadra = ({ nome, vincente, mete }) => {
  const posizione = NOME_CON_POSIZIONE.exec(nome ?? '');
  return (
    <div className={`match-team${vincente ? ' match-team--vincente' : ''}`} title={nome}>
      <span className="match-team-nome">{posizione ? posizione[1] : nome}</span>
      {posizione && <span className="match-team-posizione">{posizione[2]}</span>}
      {mete != null && <span className="match-team-mete">{mete}</span>}
    </div>
  );
};

const APlanning = () => {
  const navigate = useNavigate();
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxTurno, setMaxTurno] = useState(0);
  const [maxCampo, setMaxCampo] = useState(0);
  const [unassignedMatches, setUnassignedMatches] = useState([]);
  const [draggedMatch, setDraggedMatch] = useState(null);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, type: null, index: null });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [editingArbitro, setEditingArbitro] = useState(null);
  const [squadreList, setSquadreList] = useState([]);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [torneoStato, setTorneoStato] = useState(null); // { stato, avvio, chiusura }
  // La ricerca: `ricerca` e' quello che si sta scrivendo, `squadraCercata` quello che sta
  // effettivamente spegnendo le card. Le due si riallineano dopo il debounce, o subito se il nome
  // arriva da un suggerimento.
  const [ricerca, setRicerca] = useState('');
  const [squadraCercata, setSquadraCercata] = useState('');
  const [suggerimentiAperti, setSuggerimentiAperti] = useState(false);
  // Confini delle bande delle fasi spostati a mano: { [fase]: primoTurno }. Chi non compare usa
  // il primo turno in cui la fase ha una partita.
  const [confiniFasi, setConfiniFasi] = useState({});
  const [confineInTrascinamento, setConfineInTrascinamento] = useState(null);

  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      setEdizioneSelezionata(edizione);
      loadPlanning(edizione.id);
      loadSquadre(edizione.id);
      loadStato(edizione.id);
      loadConfiniFasi(edizione.id);
    } else {
      setLoading(false);
      setError('Nessuna edizione selezionata');
    }
  }, []);

  const loadStato = async (idTorneo) => {
    const response = await API.torneo.dettaglio(idTorneo);
    if (response.isSuccess && response.data) {
      setTorneoStato({
        stato: response.data.stato,
        avvio: response.data.avvio,
        chiusura: response.data.chiusura,
      });
    }
  };

  // Transizioni di stato del torneo (vedi docs/superpowers/specs/2026-06-06-ciclo-vita-torneo-design.md)
  const handleTransizione = async (apiCall, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    const response = await apiCall(edizioneSelezionata.id);
    if (response.isSuccess && response.data) {
      setTorneoStato(response.data); // { avvio, chiusura, stato }
    } else {
      alert(response.data || "Operazione non riuscita");
    }
  };

  const STATO_LABELS = {
    preparazione: 'In preparazione',
    inCorso: 'In corso',
    chiuso: 'Chiuso',
  };

  // l'annullamento della chiusura è possibile solo entro 12 ore (stessa regola del server)
  const annullaChiusuraPossibile = torneoStato?.chiusura
    ? Date.now() - new Date(torneoStato.chiusura).getTime() <= 12 * 60 * 60 * 1000
    : false;

  // Due secondi dall'ultimo tasto premuto prima di spegnere le card: chi scrive un nome intero non
  // vede la griglia lampeggiare a ogni lettera. Il timer riparte a ogni modifica del testo.
  useEffect(() => {
    const attesa = setTimeout(() => setSquadraCercata(ricerca.trim()), 2000);
    return () => clearTimeout(attesa);
  }, [ricerca]);

  // I suggerimenti sono i nomi che stanno davvero nel planning: un segnaposto di formula
  // ("girone Girone A #3") non ha un id di squadra, e cercarlo non accenderebbe niente.
  const squadreNelPlanning = Array.from(new Set(
    [...matches, ...unassignedMatches].flatMap(m => [
      m.s1Id > 0 ? m.squadra1 : null,
      m.s2Id > 0 ? m.squadra2 : null,
    ]).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  // Solo le prime: la lista non scorre, chi non le trova restringe la ricerca scrivendo.
  const PRIMI_SUGGERIMENTI = 5;
  const suggerimenti = (ricerca.trim()
    ? squadreNelPlanning.filter(nome => nome.toLowerCase().includes(ricerca.trim().toLowerCase()))
    : squadreNelPlanning).slice(0, PRIMI_SUGGERIMENTI);

  const applicaRicerca = (nome) => {
    setRicerca(nome);
    setSquadraCercata(nome.trim());
    setSuggerimentiAperti(false);
  };

  const pulisciRicerca = () => {
    setRicerca('');
    setSquadraCercata('');
    setSuggerimentiAperti(false);
  };

  // Una card resta accesa se la squadra cercata ci gioca o ci arbitra.
  const riguardaLaRicerca = (match) => {
    const cercata = squadraCercata.toLowerCase();
    return [match.squadra1, match.squadra2, match.squadraA]
      .some(nome => nome && nome.toLowerCase().includes(cercata));
  };

  const classeCard = (match) => (squadraCercata && !riguardaLaRicerca(match) ? ' match-card--spenta' : '');

  const renderCerca = () => (
    <div className="planning-cerca">
      <i className="fas fa-search planning-cerca-icona"></i>
      <input
        type="text"
        className="planning-cerca-input"
        value={ricerca}
        placeholder="Cerca una squadra"
        onChange={(e) => { setRicerca(e.target.value); setSuggerimentiAperti(true); }}
        onFocus={() => setSuggerimentiAperti(true)}
        // Il blur arriva prima del click sul suggerimento: la lista si chiude un attimo dopo.
        onBlur={() => setTimeout(() => setSuggerimentiAperti(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') applicaRicerca(e.target.value);
          if (e.key === 'Escape') pulisciRicerca();
        }}
      />
      {ricerca && (
        <button type="button" className="planning-cerca-pulisci" onClick={pulisciRicerca} title="Pulisci la ricerca">
          <i className="fas fa-times"></i>
        </button>
      )}
      {suggerimentiAperti && suggerimenti.length > 0 && (
        <ul className="planning-cerca-suggerimenti">
          {suggerimenti.map(nome => (
            <li key={nome}>
              {/* onMouseDown e non onClick: il blur dell'input chiuderebbe la lista prima del click */}
              <button type="button" onMouseDown={() => applicaRicerca(nome)}>{nome}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderStatoBar = () => {
    // a torneo chiuso definitivamente (oltre 12h) non c'è nulla da fare: niente barra
    if (torneoStato?.stato === 'chiuso' && !annullaChiusuraPossibile) return null;

    return (
    <div className="planning-stato-bar">
      <span className={`planning-stato-badge stato-${torneoStato?.stato || 'preparazione'}`}>
        <i className="fas fa-circle"></i>
        {STATO_LABELS[torneoStato?.stato] || 'Caricamento...'}
      </span>
      {torneoStato?.stato === 'preparazione' && (
        <button
          className="planning-stato-btn avvia"
          onClick={() => handleTransizione(API.torneo.avvia, "Avviare il torneo? Iscrizioni, modifica squadre e formula verranno bloccate e sarà possibile inserire le partite.")}
          title="Blocca iscrizioni, modifiche squadre e formula; abilita l'inserimento delle partite"
        >
          <i className="fas fa-play"></i> Avvia torneo
        </button>
      )}
      {torneoStato?.stato === 'inCorso' && (
        <>
          <button
            className="planning-stato-btn chiudi"
            onClick={() => handleTransizione(API.torneo.chiudi, "Chiudere il torneo? Partite e planning non saranno più modificabili e le statistiche diventeranno definitive.")}
            title="Congela partite e planning; le statistiche diventano definitive"
          >
            <i className="fas fa-flag-checkered"></i> Chiudi torneo
          </button>
          <button
            className="planning-stato-btn annulla"
            onClick={() => handleTransizione(API.torneo.annullaAvvio, "Annullare l'avvio del torneo? Possibile solo se non sono state inserite partite.")}
            title="Riporta il torneo in preparazione (solo se non ci sono partite inserite)"
          >
            <i className="fas fa-undo"></i> Annulla avvio
          </button>
        </>
      )}
      {torneoStato?.stato === 'chiuso' && annullaChiusuraPossibile && (
        <button
          className="planning-stato-btn annulla"
          onClick={() => handleTransizione(API.torneo.annullaChiusura, "Annullare la chiusura del torneo? Possibile solo entro 12 ore dalla chiusura.")}
          title="Riporta il torneo in corso (solo entro 12 ore dalla chiusura)"
        >
          <i className="fas fa-undo"></i> Annulla chiusura
        </button>
      )}
      {renderCerca()}
    </div>
    );
  };

  const loadPlanning = async (idTorneo) => {
    try {
      setLoading(true);
      const response = await API.planning.create(idTorneo);
      
      if (response.isSuccess && response.data) {
        // Le partite in panchina arrivano nella stessa lista, in turno 0: vanno separate prima di
        // misurare la griglia, o il loro campo (che li' e' un numero di posto) la falserebbe.
        const planningData = response.data.filter(m => m.turno !== TURNO_PANCHINA);
        setMatches(planningData);
        setUnassignedMatches(response.data.filter(m => m.turno === TURNO_PANCHINA));

        // Calculate max turno and campo
        const maxT = planningData.length ? Math.max(...planningData.map(m => m.turno)) : 0;
        const maxC = planningData.length ? Math.max(...planningData.map(m => m.campo)) : 0;
        setMaxTurno(maxT);
        setMaxCampo(maxC);
      } else {
        setError('Errore nel caricamento del planning');
      }
    } catch (err) {
      console.error('Error loading planning:', err);
      setError('Errore durante il caricamento del planning');
    } finally {
      setLoading(false);
    }
  };

  const loadConfiniFasi = async (idTorneo) => {
    const response = await API.planning.confiniFasi(idTorneo);
    if (response.isSuccess && Array.isArray(response.data)) {
      setConfiniFasi(Object.fromEntries(response.data.map(c => [c.fase, c.primoTurno])));
    }
  };

  // Solo id e nome: la lista serve alla tendina della squadra arbitrante, che cerca per nome e
  // salva l'id. La distinta la chiede chi stampa i referti, e la chiede allora.
  const loadSquadre = async (idTorneo) => {
    try {
      const response = await API.accettazione.elenco(idTorneo);
      if (response.isSuccess && response.data) {
        setSquadreList(response.data);
      }
    } catch (err) {
      console.error('Error loading squadre:', err);
    }
  };

  const getMatchAtPosition = (turno, campo) => {
    return matches.find(m => m.turno === turno && m.campo === campo);
  };

  const getMatchWinner = (match) => {
    if (!match.risultato) return null;
    
    const { mete1, pieni1, vuoti1, mete2, pieni2, vuoti2 } = match.risultato;
    
    // Calcola il punteggio totale per ogni squadra
    const score1 = (mete1 || 0);
    const score2 = (mete2 || 0) + (pieni2 || 0) + (vuoti2 || 0);
    
    if (mete1 > mete2) return '1';
    if (mete1 < mete2) return '2';
    return 'X'; // Pareggio
  };

  // Colori per i gironi - palette BRILLANTE/MEDIA (toni saturi ma non scuri)
  const gironeColors = [
    'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', // Blu brillante
    'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', // Rosso brillante
    'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)', // Verde smeraldo
    'linear-gradient(135deg, #f39c12 0%, #d68910 100%)', // Arancione dorato
    'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)', // Viola
    'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)', // Turchese
    'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)', // Rosa fucsia
    'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)', // Ciano
    'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', // Arancione
    'linear-gradient(135deg, #8bc34a 0%, #7cb342 100%)', // Verde lime
    'linear-gradient(135deg, #673ab7 0%, #5e35b1 100%)', // Viola profondo
    'linear-gradient(135deg, #009688 0%, #00897b 100%)', // Teal
    'linear-gradient(135deg, #ff5722 0%, #f4511e 100%)', // Arancio rosso
    'linear-gradient(135deg, #03a9f4 0%, #039be5 100%)', // Azzurro cielo
    'linear-gradient(135deg, #cddc39 0%, #c0ca33 100%)', // Lime giallo
    'linear-gradient(135deg, #ff4081 0%, #f50057 100%)', // Rosa acceso
    'linear-gradient(135deg, #7c4dff 0%, #651fff 100%)', // Viola elettrico
    'linear-gradient(135deg, #4caf50 0%, #43a047 100%)', // Verde
    'linear-gradient(135deg, #ffc107 0%, #ffb300 100%)', // Ambra
    'linear-gradient(135deg, #795548 0%, #6d4c41 100%)', // Marrone
  ];

  // Colori per le dirette - palette SCURA/INTENSA (toni forti e saturi)
  const diretteColors = [
    'linear-gradient(135deg, #c0392b 0%, #922b21 100%)', // Rosso scuro - Fascia 1
    'linear-gradient(135deg, #d35400 0%, #a04000 100%)', // Arancione scuro - Fascia 2
    'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)', // Viola scuro - Fascia 3
    'linear-gradient(135deg, #16a085 0%, #117a65 100%)', // Teal scuro - Fascia 4
    'linear-gradient(135deg, #2980b9 0%, #1f618d 100%)', // Blu scuro - Fascia 5
    'linear-gradient(135deg, #c2185b 0%, #9c1458 100%)', // Magenta scuro - Fascia 6
    'linear-gradient(135deg, #00838f 0%, #006064 100%)', // Ciano scuro - Fascia 7
    'linear-gradient(135deg, #558b2f 0%, #33691e 100%)', // Verde oliva scuro - Fascia 8
    'linear-gradient(135deg, #bf360c 0%, #8d2608 100%)', // Arancio bruciato - Fascia 9
    'linear-gradient(135deg, #37474f 0%, #263238 100%)', // Grigio antracite - Fascia 10
  ];

  // Estrae la fascia dal nome delle dirette (es. "Dirette 1-A" -> 1)
  const extractFasciaFromNome = (nome) => {
    if (!nome) return -1;
    // Il nome è nel formato "NomeFascia-NomeMatch" o solo "NomeFascia"
    const fasciaName = nome.includes('-') ? nome.split('-')[0] : nome;
    // Estrae il numero dalla stringa (es. "Dirette 1" -> 1)
    const match = fasciaName.match(/\d+/);
    return match ? parseInt(match[0], 10) : -1;
  };

  const getMatchColor = (match) => {
    if (!match) return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)';

    const tipo = match.tipo?.toLowerCase();
    const nome = match.nome;

    // Per le dirette, raggruppa per fascia
    if (tipo === 'dirette') {
      const fascia = extractFasciaFromNome(nome);
      if (fascia > 0) {
        // Usa l'indice della fascia per il colore (fascia 1 = indice 0)
        const colorIndex = (fascia - 1) % diretteColors.length;
        return diretteColors[colorIndex];
      }
      // Fallback per dirette senza numero di fascia
      return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
    }

    // Per i gironi, usa l'hash del nome
    if (!nome) return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)';

    const hash = nome.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    const index = Math.abs(hash) % gironeColors.length;
    return gironeColors[index];
  };

  // Mantiene la vecchia funzione per retrocompatibilità
  const getGironeColor = (girone) => {
    if (!girone) return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)';

    const hash = girone.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    const index = Math.abs(hash) % gironeColors.length;
    return gironeColors[index];
  };

  // Durante il trascinamento le bande si ridisegnano sul confine provvisorio, cosi' si vede dove
  // si sta andando prima di rilasciare.
  const confiniEffettivi = confineInTrascinamento
    ? { ...confiniFasi, [confineInTrascinamento.fase]: confineInTrascinamento.turno }
    : confiniFasi;
  const fasiRaggruppate = raggruppaTurniPerFase(matches, maxTurno, confiniEffettivi);

  // La banda che copre un turno. Si guarda l'intervallo e non i turni con partite, cosi' anche
  // una colonna vuota dentro una banda prende il colore della sua fase invece di restare orfana.
  const bandaDelTurno = (turno) =>
    fasiRaggruppate.find((f) => turno >= f.startTurno && turno <= f.endTurno) ?? null;

  // Quante partite sono gia' state battute su quante ce ne sono: e' il risultato a dire che una
  // partita e' inserita. Le partite in panchina stanno in unassignedMatches e non contano, che e'
  // giusto: non sono in programma.
  const conteggio = (dentro) => {
    const scelte = matches.filter(dentro);
    return `${scelte.filter((m) => m.risultato).length}/${scelte.length}`;
  };

  // --- Confini delle bande -----------------------------------------------------------------
  // La maniglia sul bordo destro di una banda sposta il confine con la banda successiva: una si
  // allarga, l'altra si stringe di altrettanto, e ogni banda tiene almeno una colonna. Il numero
  // salvato e' sempre il primo turno della banda di destra.

  const salvaConfine = async (fase, primoTurno) => {
    const precedente = confiniFasi;
    // Ottimistico: la banda si muove subito, e si torna indietro solo se il server rifiuta.
    setConfiniFasi(prev => {
      const next = { ...prev };
      if (primoTurno === null) delete next[fase];
      else next[fase] = primoTurno;
      return next;
    });

    const response = await API.planning.impostaConfineFase(edizioneSelezionata.id, fase, primoTurno);
    if (!response.isSuccess) {
      setConfiniFasi(precedente);
      alert(response.data || response.errorMsg || 'Non sono riuscito a spostare il confine della fase');
    }
  };

  // Il turno sotto il puntatore, letto dalle celle vere: la tabella e' a colonne fisse ma la
  // larghezza dipende da zoom e viewport, quindi non la si puo' calcolare a mano.
  const turnoSottoIlPuntatore = (clientX) => {
    const celle = document.querySelectorAll('.planning-table thead tr:nth-child(2) th');
    for (let i = 1; i < celle.length; i++) {
      const r = celle[i].getBoundingClientRect();
      if (clientX < r.right) return i; // le celle partono dall'angolo vuoto, quindi i === turno
    }
    return celle.length - 1;
  };

  const iniziaTrascinamento = (e, indiceBanda) => {
    e.preventDefault();
    e.stopPropagation();

    const bandaDestra = fasiRaggruppate[indiceBanda + 1];
    if (!bandaDestra) return;

    // Il confine puo' muoversi solo lasciando una colonna a ciascuna delle due bande.
    const minimo = fasiRaggruppate[indiceBanda].startTurno + 1;
    const massimo = bandaDestra.endTurno;

    setConfineInTrascinamento({ fase: bandaDestra.fase, turno: bandaDestra.startTurno });

    const muovi = (ev) => {
      const turno = Math.min(Math.max(turnoSottoIlPuntatore(ev.clientX) + 1, minimo), massimo);
      setConfineInTrascinamento(prev => (prev && prev.turno === turno ? prev : { fase: bandaDestra.fase, turno }));
    };

    const rilascia = (ev) => {
      document.removeEventListener('mousemove', muovi);
      document.removeEventListener('mouseup', rilascia);
      const turno = Math.min(Math.max(turnoSottoIlPuntatore(ev.clientX) + 1, minimo), massimo);
      setConfineInTrascinamento(null);
      if (turno !== bandaDestra.startTurno) salvaConfine(bandaDestra.fase, turno);
    };

    document.addEventListener('mousemove', muovi);
    document.addEventListener('mouseup', rilascia);
  };

  const confineAutomatico = (indiceBanda) => {
    const bandaDestra = fasiRaggruppate[indiceBanda + 1];
    if (bandaDestra && confiniFasi[bandaDestra.fase] !== undefined) salvaConfine(bandaDestra.fase, null);
  };
  const fuoriFase = partiteFuoriFase(matches, fasiRaggruppate);
  const isFuoriFase = (match) => match && fuoriFase.includes(match);

  // Una squadra (o un posto di formula non ancora risolto) che gioca due volte nello stesso turno.
  const doppiImpegni = doppiImpegniPerTurno(matches);
  const partiteDoppie = partiteInDoppioImpegno(doppiImpegni);
  const haDoppioImpegno = (match) => Boolean(match) && partiteDoppie.has(match);

  // A torneo chiuso il server rifiuta ogni modifica del planning: meglio non far partire nemmeno
  // il trascinamento, invece di far vedere una partita che si sposta e poi torna indietro.
  const planningBloccato = torneoStato?.stato === 'chiuso';


  const handleCalculateFase = (fase) => { 
    API.planning.calcolaFase(edizioneSelezionata.id, {faseMassima: fase+1}).then(r => {
      if(r.isSuccess){
        loadPlanning(edizioneSelezionata.id);
      }
    })
  };

  // Le distinte arrivano da chi chiama, che le ha gia' chieste per il PDF: prima questa funzione
  // le richiedeva per conto suo, e stampare una fase voleva dire due volte l'edizione intera.
  const convertMatchesToRefertoFormat = (matchesArray, squadreConGiocatori = []) => {
    return matchesArray.map(match => {
      // Trova le squadre corrispondenti per nome
      const squadra1Data = squadreConGiocatori.find(s => s.nome === match.squadra1);
      const squadra2Data = squadreConGiocatori.find(s => s.nome === match.squadra2);

      return {
        squadra1: {
          nome: match.squadra1,
          giocatori: squadra1Data?.giocatori?.map(g => ({
            numero: g.n || -1,
            maglia: g.maglia || ''
          })) || []
        },
        squadra2: {
          nome: match.squadra2,
          giocatori: squadra2Data?.giocatori?.map(g => ({
            numero: g.n || -1,
            maglia: g.maglia || ''
          })) || []
        },
        campo: match.campo,
        turno: match.turno,
        nome: match.nome
      };
    });
  };

  const handlePrintReferti = async (fase) => {
    const partiteFase = matches
      .filter(match => (match.fase || 0) === fase)
      .sort((a, b) => {
        // Ordina per campo crescente, poi per turno crescente
        if (a.campo !== b.campo) return a.campo - b.campo;
        return a.turno - b.turno;
      });

    if (partiteFase.length === 0) {
      alert('Nessuna partita trovata per questa fase');
      return;
    }

    if (!edizioneSelezionata) {
      alert('Nessuna edizione selezionata');
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const squadre = await API.accettazione.distinte(edizioneSelezionata.id);

      const { pdf } = await import('@react-pdf/renderer');
      const partiteFormattate = convertMatchesToRefertoFormat(partiteFase, squadre.data);

      const blob = await pdf(<RefertoPDF squadre={squadre.data} partite={partiteFormattate} />).toBlob();
      const url = URL.createObjectURL(blob);
      
      // Apri in un nuovo tab
      const newWindow = window.open(url, '_blank');
      
      // Libera la memoria solo quando la finestra viene chiusa o dopo un tempo più lungo
      if (newWindow) {
        // Controlla se la finestra è stata chiusa per liberare la memoria
        const checkClosed = setInterval(() => {
          if (newWindow.closed) {
            URL.revokeObjectURL(url);
            clearInterval(checkClosed);
          }
        }, 1000);
        
        // Fallback: libera la memoria dopo 30 minuti se la finestra non viene chiusa
        setTimeout(() => {
          URL.revokeObjectURL(url);
          clearInterval(checkClosed);
        }, 30 * 60 * 1000);
      } else {
        // Se il popup è bloccato, libera subito la memoria
        URL.revokeObjectURL(url);
      }

      const faseNome = fase === 0 ? 'Regolare' : fase;
      console.log(`PDF referti generato per fase ${faseNome} - ${partiteFase.length} partite`);
    } catch (error) {
      console.error('Errore durante la generazione dei referti PDF:', error);
      alert('Errore durante la generazione dei referti PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDragStart = (e, match) => {
    setDraggedMatch(match);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Helper function per determinare se l'arbitro deve essere mantenuto
  const shouldKeepArbitro = (originalTurno, newTurno, currentArbitroId) => {
    // Se non c'è arbitro, return null
    if (!currentArbitroId) return null;
    // Se il turno non cambia, mantieni l'arbitro
    if (originalTurno === newTurno) return currentArbitroId;
    // Se il turno cambia, azzera l'arbitro
    return null;
  };

  // Ogni modifica del planning passa di qui. La chiamata API non lancia mai: anche su 400, 401,
  // 403 e 409 torna { isSuccess: false }, quindi il vecchio .then()/.catch() spostava la partita
  // a video pure quando il server l'aveva rifiutata - a torneo chiuso, ogni volta - e la partita
  // tornava al suo posto al primo ricaricamento, senza che nessuno avesse visto un errore.
  // Lo stato locale si tocca solo dopo un sì.
  const applicaModifiche = async (modifiche, aggiornaStato, fallback) => {
    if (modifiche.length === 0) return true;

    const response = await API.planning.modifica(modifiche);
    if (!response.isSuccess) {
      alert((typeof response.data === 'string' && response.data) || response.errorMsg || fallback);
      return false;
    }

    aggiornaStato();
    return true;
  };

  const erroreSpostamento = 'Errore durante lo spostamento della partita. Riprova.';

  const handleDrop = async (e, targetTurno, targetCampo) => {
    e.preventDefault();

    const dragged = draggedMatch;
    setDraggedMatch(null);
    if (!dragged) return;

    const oldTurno = dragged.turno;
    const oldCampo = dragged.campo;
    // Lasciata cadere dov'era: niente da salvare. Una modifica (t,c)->(t,c) verrebbe comunque
    // registrata e rigiocata per sempre a ogni lettura del planning.
    if (oldTurno === targetTurno && oldCampo === targetCampo) return;

    // Check if target position has a match
    const targetMatch = getMatchAtPosition(targetTurno, targetCampo);
    const draggedMatchUpdated = shouldKeepArbitro(oldTurno, targetTurno, dragged.idSA);

    // Anche con una partita a destinazione basta una sola modifica: lo scambio lo fa il backend,
    // che riporta la partita trovata nella posizione di partenza. Mandare le due modifiche
    // reciproche disferebbe lo scambio alla rilettura.
    const targetMatchUpdated = targetMatch
      ? shouldKeepArbitro(targetMatch.turno, oldTurno, targetMatch.idSA)
      : null;

    await applicaModifiche([{
      turnoPartenza: oldTurno,
      campoPartenza: oldCampo,
      turnoArrivo: targetTurno,
      campoArrivo: targetCampo,
      edizione: edizioneSelezionata.id,
      idSA: draggedMatchUpdated
    }], () => {
      setMatches(prev => prev.map(match => {
        if (match === dragged) {
          const updatedMatch = { ...match, turno: targetTurno, campo: targetCampo };
          if (draggedMatchUpdated === null) {
            updatedMatch.squadraA = null;
            updatedMatch.idSA = null;
          }
          return updatedMatch;
        }
        if (targetMatch && match === targetMatch) {
          const updatedTargetMatch = { ...match, turno: oldTurno, campo: oldCampo };
          if (targetMatchUpdated === null) {
            updatedTargetMatch.squadraA = null;
            updatedTargetMatch.idSA = null;
          }
          return updatedTargetMatch;
        }
        return match;
      }));
    }, erroreSpostamento);
  };

  const handleDropToUnassigned = async (e) => {
    e.preventDefault();

    const dragged = draggedMatch;
    setDraggedMatch(null);
    if (!dragged || unassignedMatches.includes(dragged)) return;

    const [messa] = postiPerTutte([dragged], unassignedMatches);

    await applicaModifiche([{
      turnoPartenza: dragged.turno,
      campoPartenza: dragged.campo,
      turnoArrivo: TURNO_PANCHINA,
      campoArrivo: messa.campo,
      edizione: edizioneSelezionata.id,
      idSA: null // Azzera sempre l'arbitro quando viene spostata nelle non assegnate
    }], () => {
      setMatches(prev => prev.filter(m => m !== dragged));
      setUnassignedMatches(prev => [...prev, messa]);
    }, erroreSpostamento);
  };

  const handleDropFromUnassigned = async (e, targetTurno, targetCampo) => {
    e.preventDefault();

    const dragged = draggedMatch;
    setDraggedMatch(null);
    if (!dragged) return;

    // Check if target position has a match
    const targetMatch = getMatchAtPosition(targetTurno, targetCampo);
    const posto = dragged.campo; // il posto in panchina che la partita libera - e che l'eventuale
                                 // partita scambiata va a occupare

    await applicaModifiche([{
      turnoPartenza: TURNO_PANCHINA,
      campoPartenza: posto,
      turnoArrivo: targetTurno,
      campoArrivo: targetCampo,
      edizione: edizioneSelezionata.id,
      idSA: null // Azzera l'arbitro quando viene da non assegnate
    }], () => {
      setUnassignedMatches(prev => targetMatch
        ? [...prev.filter(m => m !== dragged), inPanchina(targetMatch, posto)]
        : prev.filter(m => m !== dragged));

      // Add to matches at target position - azzera l'arbitro perché viene da non assegnate
      setMatches(prev => [
        ...prev.filter(m => m !== targetMatch),
        { ...dragged, turno: targetTurno, campo: targetCampo, squadraA: null, idSA: null }
      ]);
    }, erroreSpostamento);
  };

  const handleContextMenu = (e, type, index) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.pageX,
      y: e.pageY,
      type,
      index
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, type: null, index: null });
  };

  const handleMatchDoubleClick = (match) => {
    const queryParams = new URLSearchParams({
      squadra1Id: match.s1Id,
      squadra2Id: match.s2Id,
      tipo: match.tipo,
      nome: match.nome,
      fascia: match.fase || 0,
      readonly: 'true'
    });
    navigate(`/admin/partite?${queryParams.toString()}`);
  };

  const handleEditArbitro = (match) => {
    setEditingArbitro(match);
    setAutocompleteQuery(match.squadraA || '');
  };

  const handleSaveArbitro = async (squadraArbitranteNome) => {
    if (!editingArbitro) return;

    // Trova l'ID della squadra arbitrante
    const squadraArbitrante = squadraArbitranteNome
      ? squadreList.find(s => s.nome === squadraArbitranteNome)
      : null;

    const idSA = squadraArbitrante ? squadraArbitrante.id : null;

    // Salva la modifica tramite API usando l'endpoint planning/modifica
    // Uso lo stesso formato dell'endpoint modifica ma con focus sull'arbitro
    const salvato = await applicaModifiche([{
      turnoPartenza: editingArbitro.turno,
      campoPartenza: editingArbitro.campo,
      turnoArrivo: editingArbitro.turno,
      campoArrivo: editingArbitro.campo,
      edizione: edizioneSelezionata.id,
      idSA: idSA // Aggiungo l'ID della squadra arbitrante
    }], () => {
      setMatches(prev => prev.map(match =>
        match === editingArbitro
          ? { ...match, squadraA: squadraArbitranteNome, idSA: idSA }
          : match
      ));

      setUnassignedMatches(prev => prev.map(match =>
        match === editingArbitro
          ? { ...match, squadraA: squadraArbitranteNome, idSA: idSA }
          : match
      ));
    }, 'Errore durante il salvataggio della squadra arbitrante');

    if (!salvato) return;

    setEditingArbitro(null);
    setAutocompleteQuery('');
  };

  const getFilteredSquadre = () => {
    if (!editingArbitro) return [];
    
    // Trova tutte le squadre che giocano nello stesso turno della partita selezionata
    const squadreGiocanti = matches
      .filter(match => match.turno === editingArbitro.turno && match !== editingArbitro)
      .flatMap(match => [match.squadra1, match.squadra2]);
    
    // Filtra le squadre disponibili escludendo quelle che giocano nello stesso turno
    let available = squadreList.filter(squadra => 
      !squadreGiocanti.includes(squadra.nome) &&
      squadra.nome !== editingArbitro.squadra1 &&
      squadra.nome !== editingArbitro.squadra2
    );
    
    // Applica il filtro di ricerca se presente
    if (autocompleteQuery) {
      available = available.filter(squadra => 
        squadra.nome.toLowerCase().includes(autocompleteQuery.toLowerCase())
      );
    }
    
    return available.sort((a, b) => a.nome.localeCompare(b.nome));
  };

  // Lo spostamento in blocco va mandato nell'ordine in cui va rigiocato: il backend legge una
  // partita alla posizione di partenza e, se ne trova un'altra a destinazione, le scambia. Chi
  // scala i turni in avanti deve quindi partire dal turno piu' alto (la destinazione e' ancora
  // libera), chi li scala indietro dal piu' basso.
  const spostaTurni = async (daSpostare, delta, aggiornaStato, messaggio) => {
    const modifiche = [...daSpostare]
      .sort((a, b) => (delta > 0 ? b.turno - a.turno : a.turno - b.turno) || a.campo - b.campo)
      .map(match => ({
        turnoPartenza: match.turno,
        campoPartenza: match.campo,
        turnoArrivo: match.turno + delta,
        campoArrivo: match.campo,
        edizione: edizioneSelezionata.id,
        idSA: null // Azzera arbitri perche' tutte le partite cambiano turno
      }));

    return applicaModifiche(modifiche, aggiornaStato, messaggio);
  };

  const erroreTurno = 'Errore durante la modifica dei turni. Riprova.';
  const erroreCampo = 'Errore durante la modifica dei campi. Riprova.';

  const inserisciTurno = async (primoTurnoSpostato) => {
    closeContextMenu();

    const daSpostare = matches.filter(m => m.turno >= primoTurnoSpostato);
    // Con la coda della griglia vuota non c'e' niente da spostare, ma la colonna in piu' va
    // mostrata lo stesso: e' l'unico modo di posarci sopra una partita.
    if (daSpostare.length === 0) {
      setMaxTurno(prev => prev + 1);
      return;
    }

    await spostaTurni(daSpostare, +1, () => {
      setMatches(prev => prev.map(m => m.turno >= primoTurnoSpostato
        ? { ...m, turno: m.turno + 1, squadraA: null, idSA: null }
        : m));
      setMaxTurno(prev => prev + 1);
    }, erroreTurno);
  };

  const insertTurnoBefore = (turno) => inserisciTurno(turno);

  const insertTurnoAfter = (turno) => inserisciTurno(turno + 1);

  const removeTurno = async (turno) => {
    closeContextMenu();
    if (maxTurno <= 1) return;

    const matchesToRemove = matches.filter(m => m.turno === turno);
    const matchesToShift = matches.filter(m => m.turno > turno);

    // Le partite del turno cancellato finiscono in panchina, ognuna al suo posto.
    const inPanchinaOra = postiPerTutte(matchesToRemove, unassignedMatches);

    // Prima si svuota il turno, poi si scalano gli altri: al contrario, ogni partita che scala
    // troverebbe la destinazione ancora occupata e verrebbe scambiata invece che spostata.
    const modifiche = [
      ...matchesToRemove.map((match, i) => ({
        turnoPartenza: match.turno,
        campoPartenza: match.campo,
        turnoArrivo: TURNO_PANCHINA,
        campoArrivo: inPanchinaOra[i].campo,
        edizione: edizioneSelezionata.id,
        idSA: null // Azzera arbitri quando vanno nelle non assegnate
      })),
      ...[...matchesToShift]
        .sort((a, b) => a.turno - b.turno || a.campo - b.campo)
        .map(match => ({
          turnoPartenza: match.turno,
          campoPartenza: match.campo,
          turnoArrivo: match.turno - 1,
          campoArrivo: match.campo,
          edizione: edizioneSelezionata.id,
          idSA: null // Azzera arbitri perche' cambiano turno
        }))
    ];

    const aggiorna = () => {
      setUnassignedMatches(prev => [...prev, ...inPanchinaOra]);
      setMatches(prev => prev.filter(m => m.turno !== turno).map(m => m.turno > turno ? {
        ...m,
        turno: m.turno - 1,
        squadraA: null,
        idSA: null
      } : m));
      setMaxTurno(prev => prev - 1);
    };

    // Turno vuoto: non c'e' niente da salvare, sparisce solo la colonna.
    if (modifiche.length === 0) {
      aggiorna();
      return;
    }

    await applicaModifiche(modifiche, aggiorna, erroreTurno);
  };

  // Come spostaTurni, ma sui campi: il turno non cambia, quindi l'arbitro resta.
  const spostaCampi = async (daSpostare, delta, aggiornaStato) => {
    const modifiche = [...daSpostare]
      .sort((a, b) => (delta > 0 ? b.campo - a.campo : a.campo - b.campo) || a.turno - b.turno)
      .map(match => ({
        turnoPartenza: match.turno,
        campoPartenza: match.campo,
        turnoArrivo: match.turno,
        campoArrivo: match.campo + delta,
        edizione: edizioneSelezionata.id,
        idSA: match.idSA ?? null
      }));

    return applicaModifiche(modifiche, aggiornaStato, erroreCampo);
  };

  // Inserire o togliere un campo sposta partite come inserire o togliere un turno: prima non
  // passava dal server, e la griglia tornava com'era al primo ricaricamento.
  const inserisciCampo = async (primoCampoSpostato) => {
    closeContextMenu();

    const daSpostare = matches.filter(m => m.campo >= primoCampoSpostato);
    if (daSpostare.length === 0) {
      setMaxCampo(prev => prev + 1);
      return;
    }

    await spostaCampi(daSpostare, +1, () => {
      setMatches(prev => prev.map(m => m.campo >= primoCampoSpostato ? { ...m, campo: m.campo + 1 } : m));
      setMaxCampo(prev => prev + 1);
    });
  };

  const insertCampoBefore = (campo) => inserisciCampo(campo);

  const insertCampoAfter = (campo) => inserisciCampo(campo + 1);

  const removeCampo = async (campo) => {
    closeContextMenu();
    if (maxCampo <= 1) return;

    const matchesToRemove = matches.filter(m => m.campo === campo);
    const matchesToShift = matches.filter(m => m.campo > campo);

    const inPanchinaOra = postiPerTutte(matchesToRemove, unassignedMatches);

    const modifiche = [
      ...matchesToRemove.map((match, i) => ({
        turnoPartenza: match.turno,
        campoPartenza: match.campo,
        turnoArrivo: TURNO_PANCHINA,
        campoArrivo: inPanchinaOra[i].campo,
        edizione: edizioneSelezionata.id,
        idSA: null // Azzera arbitri quando vanno nelle non assegnate
      })),
      ...[...matchesToShift]
        .sort((a, b) => a.campo - b.campo || a.turno - b.turno)
        .map(match => ({
          turnoPartenza: match.turno,
          campoPartenza: match.campo,
          turnoArrivo: match.turno,
          campoArrivo: match.campo - 1,
          edizione: edizioneSelezionata.id,
          idSA: match.idSA ?? null
        }))
    ];

    const aggiorna = () => {
      setUnassignedMatches(prev => [...prev, ...inPanchinaOra]);
      setMatches(prev => prev.filter(m => m.campo !== campo).map(m => m.campo > campo ? { ...m, campo: m.campo - 1 } : m));
      setMaxCampo(prev => prev - 1);
    };

    if (modifiche.length === 0) {
      aggiorna();
      return;
    }

    await applicaModifiche(modifiche, aggiorna, erroreCampo);
  };

  // Stili per il PDF del referto
  

  // Componente PDF del referto
  
  const pTemplate = () => {

    return [{
      squadra1:{   
        nome: "SquadrA",
        giocatori:[
          { numero:1, maglia: "prov1" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          { numero:104, maglia: "prov104" },
          
        ]
      },
      squadra2:{   
        nome: "SquadrA",
        giocatori:[
          { numero:1, maglia: "prov1" },
          { numero:104, maglia: "prov104" },
        ]
      },
      campo:10,
      turno: 4
    }]
  }

  const handleGeneratePDF = async () => {
    if (!edizioneSelezionata) {
      alert('Nessuna edizione selezionata');
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const squadre = await API.accettazione.distinte(edizioneSelezionata.id);

      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(<RefertoPDF squadre={squadre.data} partite={pTemplate()} />).toBlob();
      const url = URL.createObjectURL(blob);
      
      // Apri in un nuovo tab
      const newWindow = window.open(url, '_blank');
      
      // Libera la memoria solo quando la finestra viene chiusa o dopo un tempo più lungo
      if (newWindow) {
        // Controlla se la finestra è stata chiusa per liberare la memoria
        const checkClosed = setInterval(() => {
          if (newWindow.closed) {
            URL.revokeObjectURL(url);
            clearInterval(checkClosed);
          }
        }, 1000);
        
        // Fallback: libera la memoria dopo 30 minuti se la finestra non viene chiusa
        setTimeout(() => {
          URL.revokeObjectURL(url);
          clearInterval(checkClosed);
        }, 30 * 60 * 1000);
      } else {
        // Se il popup è bloccato, libera subito la memoria
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Errore durante la generazione del referto PDF:', error);
      alert('Errore durante la generazione del referto PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => closeContextMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="planning-page">
        <div className="planning-loading">
          <div className="loading-spinner"></div>
          <p>Caricamento planning...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="planning-page">
        {edizioneSelezionata && renderStatoBar()}
        <div className="planning-error">
          <h3>Errore</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="planning-page">
      {renderStatoBar()}
      {doppiImpegni.length > 0 && (
        <div className="fase-warning fase-warning--errore">
          <div className="fase-warning-title">
            <i className="fas fa-triangle-exclamation"></i>
            {doppiImpegni.length === 1
              ? 'Una squadra gioca due volte nello stesso turno'
              : `${doppiImpegni.length} squadre giocano due volte nello stesso turno`}
          </div>
          <div className="fase-warning-body">
            Nello stesso turno si gioca in contemporanea su tutti i campi: nessuna squadra puo'
            essere impegnata due volte. Sposta una delle due partite in un altro turno.
            <ul>
              {doppiImpegni.map(d => (
                <li key={`${d.turno}-${d.squadra}`}>
                  <strong>{d.squadra}</strong> — turno {d.turno}, campi{' '}
                  {d.partite.map(p => p.campo).sort((a, b) => a - b).join(', ')}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {fuoriFase.length > 0 && (
        <div className="fase-warning">
          <div className="fase-warning-title">
            <i className="fas fa-triangle-exclamation"></i>
            {fuoriFase.length} partite si giocano fuori dai turni della loro fase
          </div>
          <div className="fase-warning-body">
            Ogni fase deve occupare turni consecutivi. Queste partite sono rimaste indietro o
            avanti rispetto alla banda della loro fase: spostale nel blocco giusto, oppure
            raggruppa i nodi nel pannello Formula perche' cadano nella stessa fase.
            <ul>
              {fuoriFase
                .slice()
                .sort((a, b) => a.turno - b.turno || a.campo - b.campo)
                .map(m => (
                  <li key={`${m.turno}-${m.campo}`}>
                    <strong>{m.tipo} {m.nome}</strong> — turno {m.turno}, campo {m.campo}
                    {' '}(fase {m.fase === 0 ? 'regolare' : m.fase})
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
      <div className="planning-table-container">
        <table className="planning-table">
          <thead>
            {/* Header delle fasi */}
            <tr className="fase-header">
              <th className="empty-corner"></th>
              {fasiRaggruppate.map((faseInfo, indiceBanda) => (
                <th
                  key={`fase-${faseInfo.fase}`}
                  colSpan={faseInfo.count}
                  className="fase-header-cell"
                  style={{ '--fase-colore': coloreFase(faseInfo.fase) }}
                >
                  {/* Maniglia del confine con la banda successiva: l'ultima non ne ha una */}
                  {indiceBanda < fasiRaggruppate.length - 1 && (
                    <div
                      className={`fase-confine${confineInTrascinamento?.fase === fasiRaggruppate[indiceBanda + 1].fase ? ' trascinando' : ''}`}
                      onMouseDown={(e) => iniziaTrascinamento(e, indiceBanda)}
                      onDoubleClick={() => confineAutomatico(indiceBanda)}
                      title={
                        `Trascina per spostare il confine con ${titoloFase(fasiRaggruppate[indiceBanda + 1])}` +
                        (confiniFasi[fasiRaggruppate[indiceBanda + 1].fase] !== undefined
                          ? '\nDoppio clic per tornare al confine calcolato'
                          : '')
                      }
                    />
                  )}
                  <div className="fase-header-content">
                    <span className="fase-title">
                      {titoloFase(faseInfo)}
                      {' '}
                      <span className="fase-conteggio" title="Partite inserite sul totale della fase">
                        {conteggio((m) => (m.fase ?? 0) === faseInfo.fase)}
                      </span>
                      {confiniFasi[faseInfo.fase] !== undefined && (
                        <i className="fas fa-thumbtack fase-confine-fissato" title="Confine spostato a mano"></i>
                      )}
                    </span>
                    <div className="fase-buttons">
                      <button
                        className="fase-button calculate-btn"
                        onClick={() => handleCalculateFase(faseInfo.fase)}
                        title={`Calcola fase ${faseInfo.fase}`}
                      >
                        <i className="fas fa-calculator"></i>
                      </button>
                      <button
                        className="fase-button print-btn"
                        onClick={() => handlePrintReferti(faseInfo.fase)}
                        title={`Stampa referti fase ${faseInfo.fase}`}
                      >
                        <i className="fas fa-print"></i>
                      </button>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
            {/* Header dei turni */}
            <tr>
              <th>Campo \\ Turno</th>
              {Array.from({ length: maxTurno }, (_, i) => (
                <th
                  key={i + 1}
                  className="turno-header"
                  onContextMenu={(e) => handleContextMenu(e, 'turno', i + 1)}
                  style={{
                    cursor: 'context-menu',
                    '--fase-colore': coloreFase(bandaDelTurno(i + 1)?.fase),
                  }}
                >
                  Turno {i + 1}
                  <span className="turno-conteggio" title="Partite inserite sul totale del turno">
                    {conteggio((m) => m.turno === i + 1)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxCampo }, (_, campoIdx) => {
              const campo = campoIdx + 1;
              return (
                <tr key={campo}>
                  <td 
                    className="campo-header"
                    onContextMenu={(e) => handleContextMenu(e, 'campo', campo)}
                    style={{ cursor: 'context-menu' }}
                  >
                    Campo {campo}
                  </td>
                  {Array.from({ length: maxTurno }, (_, turnoIdx) => {
                    const turno = turnoIdx + 1;
                    const match = getMatchAtPosition(turno, campo);
                    
                    return (
                      <td
                        key={`${turno}-${campo}`}
                        className={`match-cell${isFuoriFase(match) ? ' match-cell--fuori-fase' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => {
                          if (draggedMatch && unassignedMatches.includes(draggedMatch)) {
                            handleDropFromUnassigned(e, turno, campo);
                          } else {
                            handleDrop(e, turno, campo);
                          }
                        }}
                      >
                        {match ? (
                          <div
                            className={`match-card${classeCard(match)}`}
                            draggable={!planningBloccato}
                            onDragStart={(e) => handleDragStart(e, match)}
                            onDoubleClick={match.s1Id > 0 && match.s2Id > 0 ? () => handleMatchDoubleClick(match) : undefined}
                            style={{
                              background: getMatchColor(match),
                              cursor: match.s1Id > 0 && match.s2Id > 0 ? 'pointer' : 'grab',
                              border: haDoppioImpegno(match) ? '2px solid #e74c3c' : match.risultato ? '2px solid #333' : 'none',
                              display: 'grid',
                              gridTemplateColumns: '1fr',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '4px'
                            }}
                            title={`${match.tipo} ${match.nome}`}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                              <NomeSquadra nome={match.squadra1} vincente={['1', 'X'].includes(getMatchWinner(match))} mete={match.risultato?.mete1} />
                              <NomeSquadra nome={match.squadra2} vincente={['2', 'X'].includes(getMatchWinner(match))} mete={match.risultato?.mete2} />
                              <div style={{ 
                                borderTop: '1px solid rgba(255,255,255,0.3)', 
                                marginTop: '2px', 
                                paddingTop: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)' }}>
                                  Arbitro: {match.squadraA || 'NA'}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditArbitro(match);
                                  }}
                                  style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    borderRadius: '3px',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '8px',
                                    color: 'white'
                                  }}
                                  title="Modifica arbitro"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="empty-cell">-</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(
        <div 
          className="unassigned-matches"
          onDragOver={handleDragOver}
          onDrop={handleDropToUnassigned}
        >
          <h3>Partite Non Assegnate</h3>
          {unassignedMatches.length === 0 && (
            <p className="unassigned-empty">Trascina qui una partita per toglierla dal calendario</p>
          )}
          <div className="unassigned-list">
            {unassignedMatches.map((match, index) => (
              <div
                key={index}
                className={`match-card unassigned-match${classeCard(match)}`}
                draggable={!planningBloccato}
                onDragStart={(e) => handleDragStart(e, match)}
                onDoubleClick={match.s1Id > 0 && match.s2Id > 0 ? () => handleMatchDoubleClick(match) : undefined}
                style={{
                  background: getMatchColor(match),
                  cursor: match.s1Id > 0 && match.s2Id > 0 ? 'pointer' : 'grab',
                  border: match.risultato ? '2px solid #333' : 'none',
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px'
                }}
                title={`${match.tipo} ${match.nome}`}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                  <NomeSquadra nome={match.squadra1} vincente={['1', 'X'].includes(getMatchWinner(match))} mete={match.risultato?.mete1} />
                  <NomeSquadra nome={match.squadra2} vincente={['2', 'X'].includes(getMatchWinner(match))} mete={match.risultato?.mete2} />
                  <div style={{ 
                    borderTop: '1px solid rgba(255,255,255,0.3)', 
                    marginTop: '2px', 
                    paddingTop: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)' }}>
                      Arbitro: {match.squadraA || 'NA'}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditArbitro(match);
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        borderRadius: '3px',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '8px',
                        color: 'white'
                      }}
                      title="Modifica arbitro"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Context Menu */}
      {contextMenu.show && (
        <div 
          className="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.type === 'turno' ? (
            <>
              <div 
                className="context-menu-item"
                onClick={() => insertTurnoBefore(contextMenu.index)}
              >
                <i className="fas fa-plus"></i> Inserisci Turno Prima
              </div>
              <div 
                className="context-menu-item"
                onClick={() => insertTurnoAfter(contextMenu.index)}
              >
                <i className="fas fa-plus"></i> Inserisci Turno Dopo
              </div>
              {maxTurno > 1 && !matches.some(m => m.turno === contextMenu.index) && (
                <div 
                  className="context-menu-item danger"
                  onClick={() => removeTurno(contextMenu.index)}
                >
                  <i className="fas fa-trash"></i> Rimuovi Turno
                </div>
              )}
            </>
          ) : (
            <>
              <div 
                className="context-menu-item"
                onClick={() => insertCampoBefore(contextMenu.index)}
              >
                <i className="fas fa-plus"></i> Inserisci Campo Prima
              </div>
              <div 
                className="context-menu-item"
                onClick={() => insertCampoAfter(contextMenu.index)}
              >
                <i className="fas fa-plus"></i> Inserisci Campo Dopo
              </div>
              {maxCampo > 1 && !matches.some(m => m.campo === contextMenu.index) && (
                <div 
                  className="context-menu-item danger"
                  onClick={() => removeCampo(contextMenu.index)}
                >
                  <i className="fas fa-trash"></i> Rimuovi Campo
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Popup per modifica arbitro */}
      {editingArbitro && (
        <div 
          className="arbitro-popup-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setEditingArbitro(null);
            setAutocompleteQuery('');
          }}
        >
          <div 
            className="arbitro-popup"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              minWidth: '300px',
              maxWidth: '400px',
              maxHeight: '500px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#333' }}>
                Seleziona Squadra Arbitrante
              </h3>
              <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                {editingArbitro.squadra1} vs {editingArbitro.squadra2}
              </p>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Cerca squadra..."
                value={autocompleteQuery}
                onChange={(e) => setAutocompleteQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>
            
            <div 
              style={{
                maxHeight: '250px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '16px'
              }}
            >
              {getFilteredSquadre().length > 0 ? (
                getFilteredSquadre().map((squadra, index) => (
                  <div
                    key={index}
                    onClick={() => handleSaveArbitro(squadra.nome)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: index < getFilteredSquadre().length - 1 ? '1px solid #eee' : 'none',
                      backgroundColor: squadra.nome === editingArbitro.squadraA ? '#e3f2fd' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = squadra.nome === editingArbitro.squadraA ? '#e3f2fd' : 'transparent'}
                  >
                    {squadra.nome}
                  </div>
                ))
              ) : (
                <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                  Nessuna squadra trovata
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => handleSaveArbitro('')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Rimuovi Arbitro
              </button>
              <button
                onClick={() => {
                  setEditingArbitro(null);
                  setAutocompleteQuery('');
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APlanning;